import React, { Component } from 'react';

import segmentLine from "images/Host/Countdown/Segment Line 2.png";

/**
 * CountdownCanvas
 *
 * Drop-in replacement for the 100 stacked <img> segment lines in Countdown.js.
 * Renders the tower's descending column of segment lines onto a single <canvas>
 * instead of ~100 DOM nodes, eliminating a full React reconciliation of the
 * line stack on every countdown tick.
 *
 * This component is a PASSIVE renderer: it draws whatever `value` (0..100) it is
 * handed, exactly when it is handed it. It owns no timers and no animation loop,
 * so the countdown timeline (SFX cues, flashes, final-round easing) is driven
 * entirely by Host.js exactly as before and is unaffected by this change.
 *
 * Placement matches the DOM lines: the canvas fills the same `.lineSection` box
 * (position:absolute inset:0). The line geometry replicates the flexbox layout —
 * each of `count` lines occupies an equal H/count slot, with the body line drawn
 * at LINE_RATIO of the slot height. The leading (topmost) line is drawn taller so
 * the segment image's purple top edge is exposed as the descending front / cap.
 *
 * NOTE: currently opt-in only. Countdown.js renders the original DOM tower by
 * default and only mounts this when passed useCanvas. Kept for further iteration.
 */

// Number of segment lines in the tower.
const LINE_COUNT = 100;
// Fraction of each slot occupied by a body line (rest is the thin dark separator).
// Packed tightly so the column reads solid, matching the DOM tower. The segment
// PNG is dark purple on top fading to cream; stacked tightly, each line's purple
// top is hidden by the line above, so the column reads cream — exactly as the DOM.
const LINE_RATIO = 0.8;
// Height of the leading (topmost) line, in slots. Drawn taller than the body lines
// so its own purple top edge is exposed as the descending front / resting cap,
// the same way the DOM's topmost line reads purple. Uses the real PNG, not a fill.
const TOP_LINE_RATIO = 1.8;
// Cap on the backing-store device pixel ratio. Keeps thin lines crisp on high-DPI
// displays while bounding fill cost; TVs report DPR 1 so they are unaffected.
const MAX_DPR = 2;

export default class CountdownCanvas extends Component {

    constructor(props) {
        super(props);
        this.canvasRef = React.createRef();
        this.img = null;
        this.imgReady = false;
        this.resizeObserver = null;
        this.rafId = null;
    }

    componentDidMount() {
        // Preload the segment image once, then draw.
        const img = new Image();
        img.onload = () => {
            this.img = img;
            this.imgReady = true;
            this.draw();
        };
        img.src = segmentLine;

        // Re-sync the backing store and repaint on any layout change (resizing
        // the backing store clears it, so we must redraw).
        if (typeof ResizeObserver !== 'undefined' && this.canvasRef.current) {
            this.resizeObserver = new ResizeObserver(() => this.sizeCanvas());
            this.resizeObserver.observe(this.canvasRef.current);
        }

        this.sizeCanvas();

        // A statically-mounted tower (e.g. the final round faded in after a
        // video) may not be laid out at its final size on this synchronous pass,
        // so force one more sized redraw after layout. Without a value change
        // nothing else would repaint it, and the leading purple bar would be lost.
        this.rafId = requestAnimationFrame(() => this.sizeCanvas());
    }

    componentDidUpdate(prevProps) {
        // Repaint on any state that changes what the tower should show, not just
        // the value — keeps the static/finished final-round tower correct.
        if (prevProps.value !== this.props.value
            || prevProps.countdownFinished !== this.props.countdownFinished) {
            this.draw();
        }
    }

    componentWillUnmount() {
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }

    // Size the backing store to the CSS display rect at the device pixel ratio,
    // capped at MAX_DPR. The tower is ~100 thin bright lines; a 1x backing store
    // gets upscaled on high-DPI displays and smears each line, dimming the column.
    // Sizing at device resolution keeps the lines as crisp as the old <img> tower.
    // The cap bounds cost on high-DPI panels; Fire TV / Roku report DPR 1, so this
    // is free for them, and drawing is event-driven (per value change), not a loop.
    sizeCanvas() {
        const canvas = this.canvasRef.current;
        if (!canvas) return;

        const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
        const rect = canvas.getBoundingClientRect();
        const w = Math.max(1, Math.round(rect.width * dpr));
        const h = Math.max(1, Math.round(rect.height * dpr));

        if (canvas.width !== w || canvas.height !== h) {
            canvas.width = w;
            canvas.height = h;
        }
        this.draw();
    }

    draw() {
        const canvas = this.canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const W = canvas.width;
        const H = canvas.height;

        ctx.clearRect(0, 0, W, H);

        if (!this.imgReady) return;

        const value = Math.max(0, Math.min(LINE_COUNT, this.props.value ?? 0));
        if (value <= 0) return;

        const slot = H / LINE_COUNT;

        // Line 1 sits at the bottom (matches flex-direction: column-reverse).
        // The leading bar (i === value) is drawn fuller so its purple cap holds.
        for (let i = 1; i <= value; i++) {
            const ratio = i === value ? TOP_LINE_RATIO : LINE_RATIO;
            const lineH = slot * ratio;
            const bandTop = H - i * slot;          // top of this line's slot
            const y = bandTop + (slot - lineH) / 2; // centre the line in its slot
            ctx.drawImage(this.img, 0, y, W, lineH);
        }
    }

    render() {
        // Fills the same box the 100 <img> lines occupied; sits behind the flare
        // (z-index 2) and level with where the DOM lines were (z-index 1).
        return (
            <canvas
                ref={this.canvasRef}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 1,
                    pointerEvents: 'none',
                }}
            />
        );
    }
}
